import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight, ChevronLeft, ArrowLeft, CalendarRange, UserPlus, Building2, Archive } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { fmtEur, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import DataModule from "./DataModule";
import { useFirma } from "../context/FirmaContext";
import { structForMonth } from "../lib/structure";
import { Button } from "./ui/button";
import { ExportButton } from "./ExportButton";
import { ExportCommercialista } from "./ExportCommercialista";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";

const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
export const monthLabel = (ym) => { if (!ym) return "—"; const [y, m] = ym.split("-"); return `${MESI[+m - 1] || m} ${y}`; };
const orderStructName = (name) => { const n = (name || "").toLowerCase(); if (n.includes("villa")) return 0; if (n.includes("boschetto")) return 1; if (n.includes("dependance") || n.includes("dépendance")) return 2; return 3; };
const STATO_LABELS = { tutti: "Tutti gli ospiti", presente: "Presenti", ospedale: "In ospedale", uscita: "Fuori struttura", adi: "Segnalati ADI" };

export default function MonthlyModule({ entity, config, baseFilter, baseDefaults, roster, activeFn, startField, unitLabel = "voci", ensure, copyPrevious, addOspite, perStructure, hideF24, footer, header }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const statoParam = roster ? searchParams.get("stato") : null;
  const { selectedStructure, structures = [], can, opSections = [] } = useAuth();
  const { activeOperatore } = useFirma();
  const permOk = (perm) => { if (!perm) return true; if (activeOperatore) return (opSections || []).includes(perm); return can(perm); };
  const qc = useQueryClient();
  const currentMonth = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`;
  // Per le anagrafiche (ospiti/operatori) si parte subito dal mese corrente; "Tutti i mesi" torna alla griglia
  const [month, setMonth] = useState(roster ? currentMonth : null);
  // Apertura rapida dai KPI dashboard: mostra subito il mese corrente filtrato per stato
  useEffect(() => {
    if (statoParam) { const now = new Date(); setMonth(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`); }
  }, [statoParam]);
  const statoFilter = (r) => {
    if (!statoParam || statoParam === "tutti") return true;
    if (statoParam === "adi") return r.autonomia === "rosso";
    if (statoParam === "presente") return !r.presenza || r.presenza === "presente";
    return r.presenza === statoParam;
  };
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [ospOpen, setOspOpen] = useState(false);
  const [osp, setOsp] = useState({});
  const m = config.monthly || {};
  const currentYear = new Date().getFullYear();

  const { data = [] } = useQuery({
    queryKey: ["mod", entity, config.structByMonth ? "all" : selectedStructure],
    queryFn: async () => (await api.get(`/${entity}`, { params: { structure_id: config.structByMonth ? "all" : selectedStructure } })).data,
  });
  const { data: ospitiKpi = [] } = useQuery({
    queryKey: ["ospiti-kpi", "all"],
    queryFn: async () => (await api.get("/ospiti", { params: { structure_id: "all" } })).data,
    enabled: entity === "pagamenti",
  });
  // Filtro struttura "per mese": un ospite appartiene alla struttura in cui si trovava quel mese (storico trasferimenti)
  const structFilter = (r, ym) => selectedStructure === "all" || structForMonth(r, ym) === selectedStructure;

  const monthOf = (r) => (m.monthKey ? r[m.monthKey] : (r[m.monthDate] || "").slice(0, 7));
  const base = baseFilter ? data.filter(baseFilter) : data;

  const yearsSet = new Set([String(currentYear), year]);
  if (roster) {
    const ys = base.map((r) => +((r[startField] || "").slice(0, 4))).filter(Boolean);
    const min = ys.length ? Math.min(...ys) : currentYear;
    for (let y = min; y <= currentYear; y++) yearsSet.add(String(y));
  } else {
    base.forEach((r) => { const k = monthOf(r); if (k) yearsSet.add(k.slice(0, 4)); });
  }
  const yearOptions = [...yearsSet].sort().reverse();

  const structMap = Object.fromEntries((structures || []).map((s) => [s.id, s]));
  const exportColumns = (config.columns || []).map((c) => ({
    header: c.label,
    accessor: (r) => { const v = c.compute ? c.compute(r) : r[c.key]; if (c.struct) return structMap[v]?.nome || ""; if (c.eur) return fmtEur(v); if (c.date) return v ? fmtDate(v) : ""; return v ?? ""; },
  }));
  const exportColor = selectedStructure === "all" ? "#1f7a5c" : (structMap[selectedStructure]?.colore || "#1f7a5c");
  const exportName = selectedStructure === "all" ? "Tutte le strutture" : (structMap[selectedStructure]?.nome || "");

  const groups = {};
  if (!roster) base.forEach((r) => {
    const k = monthOf(r);
    if (!k) return;
    if (!groups[k]) groups[k] = { count: 0, total: 0 };
    groups[k].count += 1;
    groups[k].total += Number(r[m.amount] || 0);
  });

  if (month) {
    let filterFn, defaults;
    if (roster) {
      filterFn = (r) => activeFn(r, month) && statoFilter(r) && (!config.structByMonth || structFilter(r, month) || (r.sede_id && r.structure_id === selectedStructure)) && (!baseFilter || baseFilter(r));
      defaults = { ...(baseDefaults || {}), ...(startField ? { [startField]: `${month}-01` } : {}) };
    } else {
      filterFn = (r) => monthOf(r) === month && (!baseFilter || baseFilter(r));
      defaults = { ...(baseDefaults || {}), ...(m.monthKey ? { [m.monthKey]: month } : { [m.monthDate]: `${month}-15` }) };
    }
    const currentCount = roster ? base.filter((r) => activeFn(r, month) && (!config.structByMonth || structFilter(r, month))).length : (groups[month]?.count || 0);
    const saveOspite = async () => {
      const sid = osp.structure_id || (selectedStructure !== "all" ? selectedStructure : null);
      if (!sid) { toast.error("Seleziona una struttura"); return; }
      if (!osp.nome || !osp.cognome) { toast.error("Nome e cognome obbligatori"); return; }
      const importo = Number(osp.importo || 0);
      try {
        const o = (await api.post("/ospiti", { nome: osp.nome, cognome: osp.cognome, camera: osp.camera, structure_id: sid, data_ingresso: `${month}-01`, presenza: "presente" })).data;
        const nome = `${o.nome} ${o.cognome}`;
        if (importo > 0) await api.post("/rette", { ospite_id: o.id, ospite_nome: nome, importo, structure_id: sid });
        await api.post("/pagamenti", { ospite_id: o.id, ospite_nome: nome, mese: month, retta_prevista: importo, importo_incassato: 0, differenza: importo, stato: "da incassare", structure_id: sid });
        toast.success("Ospite aggiunto");
        setOspOpen(false); setOsp({});
        await qc.invalidateQueries({ queryKey: ["mod", entity] });
      } catch (e) { toast.error("Errore nella creazione"); }
    };
    const shiftMonth = (delta) => {
      const [y, m] = month.split("-").map(Number);
      const d = new Date(y, m - 1 + delta, 1);
      const ny = d.getFullYear();
      const nm = String(d.getMonth() + 1).padStart(2, "0");
      setYear(String(ny));
      setMonth(`${ny}-${nm}`);
    };
    return (
      <div>
        <div className="mb-3 flex items-center justify-between gap-2">
          <Button variant="ghost" onClick={() => setMonth(null)} data-testid="month-back"><ArrowLeft size={16} className="mr-1" /> Tutti i mesi</Button>
          {(entity === "ospiti" || entity === "pagamenti") && (
            <Button variant="outline" onClick={() => navigate("/ospiti/archivio")} data-testid="mm-archivio-btn"><Archive size={15} className="mr-1" /> Archivio</Button>
          )}
          {entity === "operatori" && (
            <Button variant="outline" onClick={() => navigate("/operatori/archivio")} data-testid="mm-operatori-archivio-btn"><Archive size={15} className="mr-1" /> Archivio</Button>
          )}
        </div>
        {roster && statoParam && (
          <div className="mb-4 flex items-center gap-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-2.5" data-testid="mm-stato-banner">
            <span className="text-sm font-bold text-primary">Filtro: {STATO_LABELS[statoParam] || "Ospiti"}</span>
            <button onClick={() => setSearchParams({})} className="text-xs font-semibold text-muted-foreground hover:text-foreground ml-auto" data-testid="mm-stato-clear">Rimuovi filtro ✕</button>
          </div>
        )}
        <div className="mb-4 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" className="h-11 w-11" onClick={() => shiftMonth(-1)} data-testid="mm-month-prev" title="Mese precedente"><ChevronLeft size={18} /></Button>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-white shadow-sm" data-testid="mm-month-badge">
              <CalendarRange size={18} />
              <span className="font-head font-bold text-lg capitalize">{monthLabel(month)}</span>
            </div>
            <Button variant="outline" size="icon" className="h-11 w-11" onClick={() => shiftMonth(1)} data-testid="mm-month-next" title="Mese successivo"><ChevronRight size={18} /></Button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-muted-foreground">Vai al mese:</span>
            <Select value={month} onValueChange={(v) => setMonth(v)}>
              <SelectTrigger className="h-11 w-44 font-semibold" data-testid="mm-month-select"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {yearOptions.flatMap((y) => MESI.map((lbl, i) => {
                  const key = `${y}-${String(i + 1).padStart(2, "0")}`;
                  return <SelectItem key={key} value={key} data-testid={`mm-month-opt-${key}`}>{lbl} {y}</SelectItem>;
                }))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {(addOspite || copyPrevious || ensure) && (
          <div className="mb-4 flex flex-wrap gap-2">
            {addOspite && <Button size="sm" onClick={() => { setOsp({}); setOspOpen(true); }} data-testid="mm-new-ospite"><UserPlus size={15} className="mr-1" /> Nuovo ospite</Button>}
            {entity === "pagamenti" && permOk("rette") && permOk("spese") && (
              <ExportCommercialista month={month} structureId={selectedStructure} subtitle={exportName} meseLabel={monthLabel(month)} color={exportColor} />
            )}
          </div>
        )}
        {(roster || entity === "pagamenti") && (() => {
          const isOp = entity === "operatori";
          const ospitiList = entity === "pagamenti" ? ospitiKpi : base;
          const sField = isOp ? "data_assunzione" : "data_ingresso";
          const eField = isOp ? "data_cessazione" : "data_dimissione";
          const periodsOf = (r) => (r.periodi && r.periodi.length) ? r.periodi : [{ ingresso: r[sField], dimissione: r[eField] }];
          const present = (r, ym) => periodsOf(r).some((p) => { const i = (p.ingresso || "").slice(0, 7); const o = (p.dimissione || "").slice(0, 7); return (!i || i <= ym) && (!o || o >= ym); });
          // "Totali" = esattamente le righe visibili nella lista (stesso filterFn della tabella),
          // così il box e il conteggio "N ospiti in totale" coincidono sempre (dimessi del mese inclusi).
          const useSM = entity === "ospiti" || entity === "pagamenti";
          const smOk = (r, ym) => !useSM || structFilter(r, ym);
          const presentRows = roster ? base.filter((r) => filterFn(r) && (!config.structByMonth || structFilter(r, month))) : ospitiList.filter((r) => present(r, month) && smOk(r, month));
          const nuovi = ospitiList.filter((r) => smOk(r, month) && periodsOf(r).some((p) => (p.ingresso || "").slice(0, 7) === month)).length;
          const dimessi = ospitiList.filter((r) => smOk(r, month) && periodsOf(r).some((p) => (p.dimissione || "").slice(0, 7) === month)).length;
          const isBlocco = (r) => !!(r.blocco_posto_mesi || {})[month];
          // Dimissione "anticipata" = prima dell'ultimo giorno del mese. Chi è dimesso l'ultimo giorno ha concluso il mese e resta tra gli effettivi.
          const _fineMese = (() => { const [yy, mm] = month.split("-").map(Number); const ld = new Date(yy, mm, 0).getDate(); return `${month}-${String(ld).padStart(2, "0")}`; })();
          const dimessoAnticipato = (r) => periodsOf(r).some((p) => { const d = (p.dimissione || ""); return d && d.slice(0, 7) === month && d.slice(0, 10) < _fineMese; });
          const noun = isOp ? "Operatori" : "Ospiti";
          const boxes = [
            { label: `${noun} nuovi`, value: nuovi, color: "#1f7a5c", tid: "kpi-ospiti-nuovi" },
            { label: `${noun} ${isOp ? "cessati" : "dimessi"}`, value: dimessi, color: "#c2660c", tid: "kpi-ospiti-dimessi" },
          ];
          if (isOp) {
            boxes.push({ label: "Operatori totali", value: presentRows.length, color: "#2f6fb0", tid: "kpi-ospiti-totali" });
          } else {
            const hasBloccoPerm = permOk("blocco_posto");
            const bloccoCount = hasBloccoPerm ? presentRows.filter(isBlocco).length : 0;
            const effettivi = presentRows.filter((r) => !dimessoAnticipato(r) && !isBlocco(r)).length;
            const totale = effettivi + bloccoCount;
            boxes.push({ label: "Ospiti effettivi", value: effettivi, sub: "presenti, non dimessi né blocco posto", color: "#0d9488", tid: "kpi-ospiti-effettivi" });
            if (hasBloccoPerm) boxes.push({ label: "Blocco posto", value: bloccoCount, sub: monthLabel(month), color: "#b91c1c", tid: "kpi-posti-bloccati" });
            boxes.push({ label: "Ospiti totali", value: totale, sub: "effettivi + blocco posto", color: "#2f6fb0", tid: "kpi-ospiti-totali" });
            const allowedStructs = selectedStructure === "all" ? structures : structures.filter((s) => s.id === selectedStructure);
            const postiTot = allowedStructs.reduce((s, st) => s + Number(st.posti_accreditati || 0), 0);
            const pct = postiTot > 0 ? Math.round((totale / postiTot) * 100) : null;
            boxes.push({ label: "% Occupazione", value: pct == null ? "—" : `${pct}%`, sub: postiTot > 0 ? `${totale}/${postiTot} posti accreditati` : "posti accreditati non impostati", color: "#7c3aed", tid: "kpi-occupazione" });
            if (permOk("registro")) {
              const regCount = presentRows.filter((r) => !!(r.registro_mesi && r.registro_mesi[month])).length;
              boxes.push({ label: "Registro", value: regCount, sub: `${regCount}/${presentRows.length} registrati`, color: "#0369a1", tid: "kpi-registro" });
            }
          }
          const n = boxes.length;
          const lgCols = { 3: "lg:grid-cols-3", 4: "lg:grid-cols-4", 5: "lg:grid-cols-5", 6: "lg:grid-cols-6", 7: "lg:grid-cols-7", 8: "lg:grid-cols-8" }[n] || "lg:grid-cols-6";
          const gridCols = `sm:grid-cols-3 md:grid-cols-4 ${lgCols}`;
          return (
            <div className={`grid grid-cols-2 gap-2 mb-5 ${gridCols}`}>
              {boxes.map((b) => (
                <div key={b.tid} className="gr-card p-2.5 border-l-4" style={{ borderColor: b.color }} data-testid={b.tid}>
                  <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground leading-tight">{b.label}</div>
                  <div className="text-xl sm:text-2xl font-head font-bold mt-0.5 tabnum" style={{ color: b.color }}>{b.value}</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5 leading-tight">{b.sub || monthLabel(month)}</div>
                </div>
              ))}
            </div>
          );
        })()}
        {m.f24Field && !hideF24 && (() => {
          const rows = base.filter(filterFn);
          const f24 = rows.reduce((s, r) => s + Number(r[m.f24Field] || 0), 0);
          const tot = rows.reduce((s, r) => s + Number(r[m.amount] || 0), 0);
          return (
            <div className="grid sm:grid-cols-3 gap-4 mb-5">
              <div className="gr-card p-4 border-l-4" style={{ borderColor: "#1f7a5c" }} data-testid="spese-tot-box">
                <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Totale spese</div>
                <div className="text-2xl font-head font-bold text-primary mt-1">{fmtEur(tot)}</div>
                <div className="text-[11px] text-muted-foreground mt-1">Imponibile fatture di {monthLabel(month)}</div>
              </div>
              <div className="gr-card p-4 border-l-4" style={{ borderColor: "#7c3aed" }} data-testid="spese-f24-box">
                <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">IVA / Ritenute d'acconto (F24)</div>
                <div className="text-2xl font-head font-bold text-violet-700 mt-1">{fmtEur(f24)}</div>
                <div className="text-[11px] text-muted-foreground mt-1">Totale da versare con F24</div>
              </div>
              <div className="gr-card p-4 border-l-4" style={{ borderColor: "#c2660c" }} data-testid="spese-totf24-box">
                <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Totale con F24</div>
                <div className="text-2xl font-head font-bold text-amber-700 mt-1">{fmtEur(tot + f24)}</div>
                <div className="text-[11px] text-muted-foreground mt-1">Uscita complessiva del mese</div>
              </div>
            </div>
          );
        })()}
        {header && header(month)}
        {perStructure && selectedStructure === "all" && structures.length > 1 ? (
          <>
            <div className="font-head font-bold mb-2 flex items-center gap-2 text-primary"><Building2 size={16} /> Riepilogo — tutte le strutture</div>
            <div className="mb-6"><DataModule entity={entity} config={config} filterFn={filterFn} createDefaults={defaults} month={month} /></div>
            {[...structures].sort((a, b) => orderStructName(a.nome) - orderStructName(b.nome) || (a.nome || "").localeCompare(b.nome || "", "it")).map((s) => (
              <div key={s.id} className="mb-6 border-t-2 pt-4" data-testid={`mm-struct-${s.id}`}>
                <div className="font-head font-bold mb-2 flex items-center gap-2"><Building2 size={16} className="text-primary" /> {s.nome}</div>
                <DataModule entity={entity} config={config} filterFn={(r) => filterFn(r) && r.structure_id === s.id} createDefaults={{ ...defaults, structure_id: s.id }} month={month} />
              </div>
            ))}
          </>
        ) : (
          <DataModule entity={entity} config={config} filterFn={filterFn} createDefaults={defaults} month={month} />
        )}
        {footer && footer(month)}
        <Dialog open={ospOpen} onOpenChange={setOspOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Nuovo ospite — {monthLabel(month)}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              {selectedStructure === "all" && (
                <div className="col-span-2">
                  <Label className="text-xs font-semibold mb-1 block">Struttura *</Label>
                  <Select value={osp.structure_id || ""} onValueChange={(v) => setOsp((p) => ({ ...p, structure_id: v }))}>
                    <SelectTrigger data-testid="new-osp-struct"><SelectValue placeholder="Seleziona struttura" /></SelectTrigger>
                    <SelectContent>{structures.map((s) => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div><Label className="text-xs font-semibold mb-1 block">Nome *</Label><Input value={osp.nome || ""} onChange={(e) => setOsp((p) => ({ ...p, nome: e.target.value }))} data-testid="new-osp-nome" /></div>
              <div><Label className="text-xs font-semibold mb-1 block">Cognome *</Label><Input value={osp.cognome || ""} onChange={(e) => setOsp((p) => ({ ...p, cognome: e.target.value }))} data-testid="new-osp-cognome" /></div>
              <div><Label className="text-xs font-semibold mb-1 block">Camera/zona</Label><Input value={osp.camera || ""} onChange={(e) => setOsp((p) => ({ ...p, camera: e.target.value }))} data-testid="new-osp-camera" /></div>
              <div><Label className="text-xs font-semibold mb-1 block">Retta mensile (€)</Label><Input type="number" value={osp.importo || ""} onChange={(e) => setOsp((p) => ({ ...p, importo: e.target.value }))} data-testid="new-osp-importo" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOspOpen(false)}>Annulla</Button>
              <Button onClick={saveOspite} data-testid="new-osp-save" className="font-semibold">Aggiungi ospite</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  const monthsList = Array.from({ length: 12 }, (_, i) => {
    const mm = String(i + 1).padStart(2, "0");
    const key = `${year}-${mm}`;
    if (roster) return { key, label: MESI[i], count: base.filter((r) => activeFn(r, key) && (!config.structByMonth || structFilter(r, key))).length };
    const g = groups[key] || { count: 0, total: 0 };
    return { key, label: MESI[i], ...g };
  });

  return (
    <div>
      <div className="mb-5 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-head font-bold">{config.title}</h1>
          <p className="text-sm text-muted-foreground">Seleziona un mese per {roster ? "vedere chi era presente" : "vederne la composizione"}</p>
        </div>
        <div className="flex items-center gap-2">
          {roster && entity === "ospiti" && (
            <Button variant="outline" onClick={() => navigate("/ospiti/archivio")} data-testid="ospiti-archivio-btn"><Archive size={15} className="mr-1" /> Archivio</Button>
          )}
          {roster && entity === "operatori" && (
            <Button variant="outline" onClick={() => navigate("/operatori/archivio")} data-testid="operatori-archivio-btn"><Archive size={15} className="mr-1" /> Archivio</Button>
          )}
          {entity === "pagamenti" && (
            <Button variant="outline" onClick={() => navigate("/ospiti/archivio")} data-testid="rette-archivio-btn"><Archive size={15} className="mr-1" /> Archivio</Button>
          )}
          <ExportButton filename={entity} title={config.title} subtitle={exportName} columns={exportColumns} rows={base} color={exportColor} testid={`export-${entity}`} />
          <span className="text-sm font-semibold text-muted-foreground">Anno:</span>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="w-28 font-semibold" data-testid="year-selector"><SelectValue /></SelectTrigger>
            <SelectContent>{yearOptions.map((y) => <SelectItem key={y} value={y} data-testid={`year-${y}`}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {monthsList.map((mo) => (
          <button key={mo.key} onClick={async () => {
            if (ensure) { try { await api.post(`/${entity}/ensure/${mo.key}`, null, { params: { structure_id: selectedStructure } }); await qc.invalidateQueries({ queryKey: ["mod", entity] }); } catch {} }
            setMonth(mo.key);
          }} data-testid={`month-${mo.key}`}
            className={`gr-card gr-kpi p-4 text-left flex items-center justify-between hover:border-primary ${mo.count === 0 ? "opacity-70" : ""}`}>
            <div>
              <div className="font-head font-bold">{mo.label}</div>
              {roster ? (
                <><div className="text-2xl font-head font-bold text-primary tabnum mt-1">{mo.count}</div><div className="text-xs text-muted-foreground">{unitLabel}</div></>
              ) : m.amount ? (
                <><div className="text-xs text-muted-foreground mt-0.5">{mo.count} voci</div><div className="text-xl font-head font-bold text-primary tabnum mt-1">{fmtEur(mo.total)}</div></>
              ) : (
                <><div className="text-2xl font-head font-bold text-primary tabnum mt-1">{mo.count}</div><div className="text-xs text-muted-foreground">eventi</div></>
              )}
            </div>
            <ChevronRight className="text-muted-foreground" size={18} />
          </button>
        ))}
      </div>
    </div>
  );
}
